import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { UpdateOrgBody, type Org } from '@reelmachine/core';
import { keys, useConnectZernio, useCurrentOrg, useReadChannel, useUpdateOrg } from '../api/hooks';
import { Icon } from '../ui/Icon';
import { ErrorView, Loading, Screen } from '../ui/Screen';
import { useToast } from '../ui/Toast';

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong.';
}

function ChannelResult({ org }: { org: Org }) {
  const ch = org.channel!;
  const theme = org.brand?.theme;
  return (
    <div className="card">
      <span className="label">Tone</span>
      <p>
        {ch.summary}
        {ch.bestLengthS && ` Best length ${ch.bestLengthS[0]}–${ch.bestLengthS[1]} s.`}
      </p>
      {ch.patterns.length > 0 && (
        <>
          <span className="label">Patterns</span>
          <ul>
            {ch.patterns.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </>
      )}
      <p className="nums">{ch.reelsAnalyzed} reels analysed</p>
      {theme && (
        <>
          <span className="label">Design</span>
          <div className="row">
            <div className="swatch" style={{ background: theme.main }} title={`Brand ${theme.main}`} />
            <div className="swatch" style={{ background: theme.accent }} title={`Accent ${theme.accent}`} />
            <span className="nums">
              {theme.font}
              {org.brand?.logoKey ? ' · logo' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function ChannelForm({ org }: { org: Org }) {
  const toast = useToast();
  const update = useUpdateOrg(org.id);
  const read = useReadChannel(org.id);
  const [handle, setHandle] = useState(org.instagramHandle ?? '');
  const [website, setWebsite] = useState(org.website ?? '');
  const [error, setError] = useState<string | null>(null);
  const reading = org.job?.kind === 'channel' && org.job.status === 'running';
  const busy = reading || update.isPending || read.isPending;

  const start = async () => {
    setError(null);
    const parsed = UpdateOrgBody.safeParse({
      instagramHandle: handle.trim() || undefined,
      website: website.trim() || undefined,
    });
    if (!parsed.success || !handle.trim()) {
      setError('Enter an Instagram handle like @channel.');
      return;
    }
    try {
      await update.mutateAsync(parsed.data);
      await read.mutateAsync();
    } catch (e) {
      setError(errorText(e));
      toast(errorText(e));
    }
  };

  return (
    <div className="card">
      <div className="field">
        <label className="label" htmlFor="ig">
          Instagram channel
        </label>
        <input
          id="ig"
          type="text"
          placeholder="@handle"
          autoCapitalize="none"
          autoCorrect="off"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="web">
          Website
        </label>
        <input
          id="web"
          type="url"
          inputMode="url"
          placeholder="example.org"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {org.job?.status === 'failed' && org.job.kind === 'channel' && (
        <p className="error-text" role="alert">
          {org.job.error ?? 'Reading the channel failed.'}
        </p>
      )}
      {reading ? (
        <div className="field" role="status">
          <span className="label">{org.job?.step ?? 'Reading channel'}…</span>
          <div className="track" aria-hidden="true">
            <i style={{ width: `${Math.round((org.job?.progress ?? 0.1) * 100)}%` }} />
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={`btn ${org.channel ? 'sec' : ''}`}
          disabled={busy}
          onClick={() => void start()}
        >
          {busy ? 'Reading…' : org.channel ? 'Read again' : 'Read channel'}
        </button>
      )}
    </div>
  );
}

function ZernioCard({ org }: { org: Org }) {
  const toast = useToast();
  const connect = useConnectZernio(org.id);
  const status = org.zernio?.status;
  const go = async () => {
    try {
      const { url } = await connect.mutateAsync();
      window.location.assign(url);
    } catch (e) {
      toast(errorText(e));
    }
  };
  return (
    <div className="card">
      <div className="row between nowrap">
        <div>
          <span className="label">Posting</span>
          <h3>Instagram via Zernio</h3>
          {status === 'expired' && <p className="small muted">The connection expired. Connect again.</p>}
        </div>
        {status === 'connected' ? (
          <span className="chip ok">
            <Icon name="check" /> Connected
          </span>
        ) : (
          <button type="button" className="iconbtn" disabled={connect.isPending} onClick={() => void go()}>
            {connect.isPending ? 'Opening…' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
}

export function ChannelSetup() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { org, orgId, me, query } = useCurrentOrg();

  // Back from Zernio: `?zernio=connected` (or `error`).
  const zernio = params.get('zernio');
  useEffect(() => {
    if (!zernio) return;
    toast(zernio === 'connected' ? 'Instagram connected' : 'Instagram could not be connected. Try again.');
    if (orgId) void qc.invalidateQueries({ queryKey: keys.org(orgId) });
    void qc.invalidateQueries({ queryKey: keys.me });
    setParams({}, { replace: true });
  }, [zernio, orgId, qc, setParams, toast]);

  if (me.isError) return <ErrorView title="Channel" back="/" error={me.error} />;
  if (query.isError) return <ErrorView title="Channel" back="/" error={query.error} />;
  if (!org) return <Loading title="Channel" />;

  const ready = !!org.channel;
  return (
    <Screen
      title="Channel"
      back="/"
      bar={
        <button type="button" className="btn" disabled={!ready} onClick={() => navigate('/')}>
          Done
        </button>
      }
      barHint={ready ? undefined : 'Read the channel first.'}
    >
      <ChannelForm org={org} />
      {org.channel && <ChannelResult org={org} />}
      <ZernioCard org={org} />
    </Screen>
  );
}
